import React, { useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { LensDataset, LensDatasetField, LensValueFormatKind } from '~/types/leLens';

import { applyAggregateFields, applyRowFields, prepareFields } from '../../datasetFields';
import { formatLensValue } from '../../lensChart';
import type { PanelShaped } from '../panelTypes';

/**
 * F5, "+ Calculated field": name, formula with autocomplete over declared
 * columns, On totals / On each row, format, a live preview from rows
 * already loaded, Save field. No warehouse call, matching the footer's "No
 * query runs".
 *
 * Reuses `datasetFields.ts`'s `prepareFields`/`applyRowFields`/
 * `applyAggregateFields`, the exact evaluator `DatasetFieldsEditor.tsx`
 * already runs and the panel's own `previewShape.ts` composes from, rather
 * than a second formula engine.
 */

const NEAR_MATCH_MIN_SCORE = 0.4;

/** A short, cheap nearest-match suggestion (F5's "refused with the nearest matches"): shares a
 *  meaningful prefix or substring with the unknown name, scored by overlap length, not a full
 *  edit-distance search, since a dataset's column list is always small. */
function nearestMatches(unknown: string, known: ReadonlyArray<string>): Array<string> {
  const needle = unknown.toLowerCase();
  return known
    .map((name) => {
      const hay = name.toLowerCase();
      const shared = hay.includes(needle) || needle.includes(hay) ? Math.min(hay.length, needle.length) : 0;
      let prefix = 0;
      while (prefix < hay.length && prefix < needle.length && hay[prefix] === needle[prefix]) {
        prefix += 1;
      }
      const score = Math.max(shared, prefix) / Math.max(hay.length, needle.length, 1);
      return { name, score };
    })
    .filter((entry) => entry.score >= NEAR_MATCH_MIN_SCORE)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((entry) => entry.name);
}

const FORMAT_OPTIONS: Array<{ value: LensValueFormatKind; label: string }> = [
  { label: 'Number', value: 'number' },
  { label: 'Percent', value: 'percent' },
  { label: 'Currency (AUD)', value: 'currency' },
];

export type CalculatedFieldProps = {
  dataset: LensDataset;
  /** Rows already loaded for this dataset, for the live preview. Preview is over the whole
   *  dataset, matching right_panel_prototype.html's Calculated field section: the panel's
   *  `datasetActionsSlot` seam does not carry the currently-edited card's own grouping. */
  loadedRows: PanelShaped | undefined;
  onSave: (nextDataset: LensDataset) => Promise<void>;
};

export default function CalculatedField({ dataset, loadedRows, onSave }: CalculatedFieldProps) {
  const [name, setName] = useState('');
  const [expression, setExpression] = useState('');
  const [when, setWhen] = useState<LensDatasetField['when']>('aggregate');
  const [format, setFormat] = useState<LensValueFormatKind>('number');
  const [saving, setSaving] = useState(false);

  const columns = loadedRows?.columns ?? (dataset.columns ?? []).map((column) => column.name);
  const trimmedName = name.trim();
  const draftField: LensDatasetField = { expression, name: trimmedName || '__preview__', when };
  const existingFields = dataset.fields ?? [];

  // Re-validated on every render rather than memoized on a stable key: the field list here is
  // always a handful of entries, so re-running the check as the reader types costs nothing.
  const prepared = prepareFields([...existingFields, draftField], columns);
  const fault = expression.trim() ? prepared.faults.find((entry) => entry.name === draftField.name)?.reason : undefined;
  const unknownNames = fault?.match(/does not return (.+)\.$/)?.[1];
  const suggestedMatches = unknownNames
    ? unknownNames
        .split(', ')
        .flatMap((unknown) => nearestMatches(unknown, columns))
        .filter((value, index, all) => all.indexOf(value) === index)
    : [];
  const suggestion = suggestedMatches.length ? `Did you mean ${suggestedMatches.join(', ')}?` : undefined;

  function totalsRow(rows: PanelShaped): Record<string, unknown> {
    const totals: Record<string, number> = {};
    rows.rows.forEach((row) => {
      Object.entries(row).forEach(([key, value]) => {
        const numeric = typeof value === 'number' ? value : Number(value);
        if (Number.isFinite(numeric)) {
          totals[key] = (totals[key] ?? 0) + numeric;
        }
      });
    });
    return totals;
  }

  const preview = (() => {
    if (!loadedRows || !expression.trim() || fault) {
      return undefined;
    }
    const ready = (when === 'row' ? prepared.row : prepared.aggregate).find(
      (entry) => entry.field.name === draftField.name,
    );
    if (!ready) {
      return undefined;
    }
    if (when === 'row') {
      const withRow = applyRowFields(loadedRows.rows, [ready]);
      const total = withRow.reduce((sum, row) => {
        const value = row[draftField.name];
        const numeric = typeof value === 'number' ? value : Number(value);
        return sum + (Number.isFinite(numeric) ? numeric : 0);
      }, 0);
      return { label: `Preview: sum across ${withRow.length.toLocaleString()} rows`, value: total };
    }
    const totalled = applyAggregateFields([totalsRow(loadedRows)], [ready], loadedRows.columns);
    return { label: 'Preview over the whole dataset', value: totalled[0]?.[draftField.name] };
  })();

  const canSave = Boolean(trimmedName) && Boolean(expression.trim()) && !fault;

  const save = async () => {
    setSaving(true);
    try {
      const nextField: LensDatasetField = { expression: expression.trim(), name: trimmedName, when };
      await onSave({
        ...dataset,
        columns: [...(dataset.columns ?? []), { kind: 'calculated', name: trimmedName, type: 'FLOAT64' }],
        fields: [...existingFields, nextField],
      });
      setName('');
      setExpression('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={1.25}>
      <TextField label="Name" onChange={(event) => setName(event.target.value)} size="small" value={name} />

      <Autocomplete
        freeSolo
        inputValue={expression}
        onInputChange={(_event, value) => setExpression(value)}
        options={columns}
        renderInput={(params) => (
          <TextField
            {...params}
            error={Boolean(fault)}
            helperText={fault ? `${fault}${suggestion ? ` ${suggestion}` : ''}` : undefined}
            label="Formula"
            slotProps={{ htmlInput: { ...params.inputProps, style: { fontFamily: 'monospace', fontSize: 12.5 } } }}
          />
        )}
      />

      <Box>
        <Typography color="text.secondary" sx={{ display: 'block', mb: 0.5 }} variant="caption">
          Evaluate
        </Typography>
        <ToggleButtonGroup exclusive onChange={(_event, value) => value && setWhen(value)} size="small" value={when}>
          <ToggleButton value="aggregate">On totals</ToggleButton>
          <ToggleButton value="row">On each row</ToggleButton>
        </ToggleButtonGroup>
        <Typography color="text.secondary" sx={{ display: 'block', mt: 0.5 }} variant="caption">
          {when === 'aggregate'
            ? 'Totals: the formula runs once per group on the summed columns. Right for rates, shares and averages at any grain.'
            : 'Each row: runs before grouping, so the result is an ordinary column you can group, filter and sum.'}
        </Typography>
      </Box>

      <TextField
        label="Format"
        onChange={(event) => setFormat(event.target.value as LensValueFormatKind)}
        select
        size="small"
        value={format}
      >
        {FORMAT_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {preview && (
        <Typography color="text.secondary" variant="caption">
          {preview.label}:{' '}
          <Box component="span" sx={{ color: 'text.primary', fontFamily: 'monospace' }}>
            {formatLensValue(
              preview.value,
              format === 'currency' ? { currency: 'AUD', kind: 'currency' } : { kind: format },
            )}
          </Box>
        </Typography>
      )}

      {!fault && expression.trim() && !preview && (
        <Alert severity="info" variant="outlined">
          No rows loaded yet for a preview; the field still saves.
        </Alert>
      )}

      <Box>
        <Button disabled={!canSave || saving} onClick={save} variant="contained">
          Save field
        </Button>
      </Box>
    </Stack>
  );
}
